import { get } from 'svelte/store';

import { user_data } from 'src/stores/account/user';
import { currentUnitId } from 'src/stores/account/home';
import { isValidJSON } from 'src/helpers/validators';
import { WHATS_NEW_SEEN_KEY } from 'src/constants';

/** Map of home `unit_id` -> changelog timestamp last acknowledged on this device. */
type WhatsNewSeenMap = Record<string, number>;

/**
 * Collect every home `unit_id` reachable in the session (active unit + owned units + shared sub-units).
 * Related accounts overlap on a shared home's `unit_id`, so this set links the whole group.
 */
const getSessionUnitIds = (): string[] => {
	const userData = get(user_data);
	const ids = new Set<string>();

	const active = get(currentUnitId);
	if (active) ids.add(String(active));

	Object.keys(userData?.available_units ?? {}).forEach((id) => id && ids.add(String(id)));
	Object.keys(userData?.available_sub_units ?? {}).forEach((id) => id && ids.add(String(id)));

	return [...ids];
};

const readSeenMap = (): WhatsNewSeenMap => {
	const raw = localStorage.getItem(WHATS_NEW_SEEN_KEY);
	if (raw && isValidJSON(raw)) {
		const parsed = JSON.parse(raw);
		if (parsed && typeof parsed === 'object') return parsed as WhatsNewSeenMap;
	}
	return {};
};

/**
 * Whether this changelog was already acknowledged on this device for any home reachable in the session.
 * @param dateTimestamp - Latest changelog release timestamp under evaluation.
 */
export const hasSeenWhatsNewOnDevice = (dateTimestamp: number): boolean => {
	const map = readSeenMap();
	return getSessionUnitIds().some((id) => {
		const seenTimestamp = map[id];
		return typeof seenTimestamp === 'number' && seenTimestamp >= dateTimestamp;
	});
};

/**
 * Mark this changelog as seen on this device for every home reachable in the session.
 * Additive to the backend per-home `whatsNew` flag — never replaces it.
 * @param dateTimestamp - Changelog release timestamp that was acknowledged.
 */
export const markWhatsNewSeenOnDevice = (dateTimestamp: number): void => {
	const ids = getSessionUnitIds();
	if (ids.length === 0) return;

	const map = readSeenMap();
	ids.forEach((id) => {
		map[id] = dateTimestamp;
	});
	localStorage.setItem(WHATS_NEW_SEEN_KEY, JSON.stringify(map));
};

/** Clear the device-local "seen What's New" cache (used by the dev reset tool). */
export const resetWhatsNewSeenOnDevice = (): void => {
	localStorage.removeItem(WHATS_NEW_SEEN_KEY);
};
